import {
  getStatementSplitData,
  IStatementColumns,
  IStatementCurrencySection,
  IStatementLine,
  StatementColumns,
  StatementLineType,
  StatementType,
} from "shared";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { ArrowDownTrayIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

import { LoadingOverlay } from "../../../../main/components/Molecules/LoadingOverlay";
import { DatePopover } from "../../../../common/Components/DatePopover";
import { formatAmount } from "../../../../common/lib/helpers";
import { useApiRequest } from "../../../../main/hooks/useApiRequest";
import { env } from "../../../../common/lib/env";
import { classNames } from "../../../../common/lib/classNames";
import { Segmented, toolbarButton } from "../../portalUi";

import { StatementHeading } from "./StatementHeading";
import { IStatementGroupHeading, StatementLines } from "./StatementLines";
import { StatementMobileLines } from "./StatementMobileLines";
import { statementToCsv } from "./statementCsv";

interface IStatementSectionProps {
  type: `outstanding` | `paid`;
  currencySection: IStatementCurrencySection;
  /** All currencies on the statement, shows a switch when more than one */
  currencies: string[];
  onCurrencyChange: (currency: string) => void;
  columnSettings: IStatementColumns;
  statementPeriod: {
    start?: string;
    end?: string;
  };
  onDownload: (currency: string, type: StatementType) => Promise<void>;
  loading?: boolean;
  organisationTimezone: string;
  childContacts?: { id?: number; name?: string }[];
  onDownloadInvoice: (line: IStatementLine) => void;
  downloadingInvoices?: Record<string, boolean>; // InvoiceId
  /** Group lines into due now and not yet due */
  showSplitBalances?: boolean;
  selectedInvoiceIds: string[];
  onToggle: (line: IStatementLine) => void;
  allowedDownloadActivity?: boolean;
  invoiceLinksUseProxy?: boolean;
  organisationId: string;
  contactHash: string;
  contactName: string;
  refetchContact: () => void;
}

export function StatementSection({
  type,
  currencySection: statement,
  currencies,
  onCurrencyChange,
  statementPeriod,
  onDownload,
  loading,
  organisationTimezone,
  childContacts = [],
  onDownloadInvoice,
  showSplitBalances,
  columnSettings = [],
  downloadingInvoices,
  selectedInvoiceIds,
  onToggle,
  allowedDownloadActivity,
  invoiceLinksUseProxy,
  organisationId,
  contactHash,
  contactName,
  refetchContact,
}: IStatementSectionProps) {
  const [downloadingSelected, setDownloadingSelected] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const request = useApiRequest();
  const navigate = useNavigate();

  const isOutstanding = type === `outstanding`;
  const allLines = isOutstanding ? statement.lines : statement.paidLines;
  const hasPaymentLinks = isOutstanding && allLines.some(line => !!line.paymentLink);

  // Overdue age doesnt make sense in Paid invoices so remove if needed, and always show the payment date
  if (!isOutstanding) {
    columnSettings = columnSettings.filter(column => column.type !== StatementColumns.OverdueAge);

    if (!columnSettings.some(column => column.type === StatementColumns.PaidDate)) {
      columnSettings = [...columnSettings, { type: StatementColumns.PaidDate }];
    }
  }

  const groups = useMemo(() => {
    const invoiceTotal = (lines: IStatementLine[]) => formatAmount(
      lines.filter(l => l.type === StatementLineType.Invoice).reduce((acc, l) => acc + l.outstandingInCents, 0),
      statement.currency,
    );
    const childIds = childContacts.map(c => c.id);
    const parentLines = allLines.filter(line => !childIds.includes(line.contactId));
    const result: { heading?: IStatementGroupHeading; lines: IStatementLine[] }[] = [];

    if (showSplitBalances && isOutstanding) {
      const notYetDue = new Set(getStatementSplitData(statement).after.lines);
      const dueNow = parentLines.filter(l => !notYetDue.has(l));
      const later = parentLines.filter(l => notYetDue.has(l));

      result.push(
        { heading: { label: `Due now`, dot: `red`, total: invoiceTotal(dueNow) }, lines: dueNow },
        { heading: { label: `Not yet due`, dot: `grey`, total: invoiceTotal(later) }, lines: later },
      );
    }
    else {
      result.push({ lines: parentLines });
    }

    childContacts.forEach(child => {
      const lines = allLines.filter(line => line.contactId === child.id);
      const total = formatAmount(lines.reduce((acc, l) => acc + l.outstandingInCents, 0), statement.currency);

      result.push({ heading: { label: child.name, child: true, total }, lines });
    });

    return result;
  }, [statement, allLines, childContacts, showSplitBalances, isOutstanding]);

  async function downloadAsPDF(statementType: StatementType) {
    setDownloading(true);
    await onDownload(statement.currency, statementType);
    setDownloading(false);
  }

  async function downloadSelectedInvoices() {
    try {
      setDownloadingSelected(true);
      // When more than 5, the response will be JSON
      if (selectedInvoiceIds.length > 5) {
        const result = await request({
          method: `GET`,
          url: `${env.basePortalApiUrl}contact/code/${contactHash}/download-multiple`,
          params: {
            invoiceIds: selectedInvoiceIds,
          },
        });

        toast.success(result.data.successMessage);
      }
      else {
        const result = await request({
          method: `GET`,
          responseType: `blob`,
          url: `${env.basePortalApiUrl}contact/code/${contactHash}/download-multiple`,
          params: {
            invoiceIds: selectedInvoiceIds,
          },
        });

        const url = window.URL.createObjectURL(new Blob([result.data]));
        const link = document.createElement(`a`);
        link.href = url;
        link.setAttribute(`download`, `${contactName}-multiple-invoices.zip`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
    catch (e) {
      console.error(e);
      // TODO: how to present error cleanly on portal
    }
    finally {
      setDownloadingSelected(false);

      // this may have updated the contact so reload
      refetchContact();
    }
  }

  function downloadAsCsv() {
    const csv = statementToCsv(allLines, columnSettings, childContacts.length > 0);
    const url = URL.createObjectURL(new Blob([csv], { type: `text/csv` }));
    const link = document.createElement(`a`);
    link.href = url;
    link.download = `${contactName}_${statement.currency}_${type}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function openInvoice(line: IStatementLine) {
    if (invoiceLinksUseProxy) {
      window.open(`${env.apiUrl}/proxy/statement-link/${organisationId}/invoice/${line.hash}`, `_blank`, `noopener,noreferrer`);
    }
    else {
      navigate(`/i/${line.hash}`);
    }
  }

  const downloadItems: { label: string; onClick: () => void; disabled?: boolean; className?: string }[] = [];

  if (isOutstanding) {
    if (allowedDownloadActivity) {
      // portal-download-activity-statement is a hook for per-org custom CSS, see orgOverrides
      downloadItems.push({
        label: `Activity Statement (PDF)`,
        onClick: () => downloadAsPDF(StatementType.ACTIVITY),
        className: `portal-download-activity-statement`,
      });
    }

    downloadItems.push({ label: `Outstanding Statement (PDF)`, onClick: () => downloadAsPDF(StatementType.OUTSTANDING) });
  }

  downloadItems.push(
    { label: `All as CSV`, onClick: downloadAsCsv },
    {
      label: `Download ${selectedInvoiceIds.length} Invoice${selectedInvoiceIds.length === 1 ? `` : `s`}`,
      onClick: downloadSelectedInvoices,
      disabled: selectedInvoiceIds.length === 0,
    },
  );

  let title = `Recently Paid Invoices`;

  if (isOutstanding) {
    title = currencies.length > 1 ? `Outstanding Statement (${statement.currency})` : `Outstanding Statement`;
  }

  let statementPeriodText = null;

  if (isOutstanding && statementPeriod.start) {
    statementPeriodText = (
      <span className={ `flex` }>
        { `All items outstanding issued in ` }
        <DatePopover
          className={ `ml-1` }
          date={ statementPeriod.start }
          labelFormat={ `LLLL yyyy` }
          time={ `start` }
          monthOnly
          organisationTimezone={ organisationTimezone }
        />
      </span>
    );
  }
  else if (isOutstanding && statementPeriod.end) {
    statementPeriodText = (
      <span className={ `flex` }>
        { `All items outstanding issued on or before ` }
        <DatePopover
          className={ `ml-1` }
          date={ statementPeriod.end }
          labelFormat={ `dd LLLL yyyy` }
          time={ `end` }
          organisationTimezone={ organisationTimezone }
        />
      </span>
    );
  }

  const lineProps = {
    organisationTimezone,
    selectedInvoiceIds,
    onToggle,
    onOpen: openInvoice,
    showPayColumn: hasPaymentLinks,
  };

  return (
    <div className={ `rounded-xl border border-gray-200 bg-white` }>
      <div className={ `flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-[clamp(1rem,3cqi,1.5rem)] py-3.5` }>
        <div className={ `flex min-w-0 flex-col gap-0.5` }>
          <h2 className={ `text-base font-semibold text-gray-900` }>{ title }</h2>
          { statementPeriodText && <span className={ `text-[0.8125rem] text-gray-500` }>{ statementPeriodText }</span> }
        </div>
        <div className={ `flex flex-wrap items-center gap-2` }>
          { currencies.length > 1 && (
            <Segmented
              options={ currencies.map(c => ({ value: c, label: c })) }
              value={ statement.currency }
              onChange={ onCurrencyChange }
            />
          ) }
          <Menu
            as={ `div` }
            className={ `relative` }
          >
            <MenuButton
              className={ toolbarButton }
              disabled={ downloading || downloadingSelected }
            >
              <ArrowDownTrayIcon className={ `h-[0.9375rem] w-[0.9375rem]` } />
              { `Download` }
              <ChevronDownIcon className={ `h-[0.8125rem] w-[0.8125rem]` } />
            </MenuButton>
            <MenuItems
              anchor={ { to: `bottom end`, gap: 6 } }
              className={ `z-20 flex w-60 flex-col rounded-[0.625rem] border border-gray-200 bg-white p-1.5 shadow-lg focus:outline-hidden` }
            >
              { downloadItems.map(item => (
                <MenuItem
                  key={ item.label }
                  disabled={ item.disabled }
                >
                  <button
                    type={ `button` }
                    disabled={ item.disabled }
                    onClick={ item.onClick }
                    className={ classNames(`flex cursor-pointer rounded-md px-3 py-2 text-left text-sm text-gray-900 data-disabled:cursor-default data-disabled:text-gray-400 data-focus:bg-paidniceGreen-50`, item.className) }
                  >
                    { item.label }
                  </button>
                </MenuItem>
              )) }
            </MenuItems>
          </Menu>
        </div>
      </div>

      <LoadingOverlay loading={ loading || downloadingSelected }>
        <div className={ `px-[clamp(0.625rem,2cqi,1rem)] pb-4 pt-2` }>
          <div className={ `sm:hidden` }>
            { groups.map((group, i) => (
              <StatementMobileLines
                key={ i }
                lines={ group.lines }
                heading={ group.heading }
                columnSettings={ columnSettings }
                downloadingInvoices={ downloadingInvoices }
                onDownload={ onDownloadInvoice }
                { ...lineProps }
              />
            )) }
          </div>

          <div className={ `hidden overflow-x-auto sm:block` }>
            <table className={ `w-full border-separate border-spacing-0` }>
              <StatementHeading
                columns={ columnSettings }
                showPayColumn={ hasPaymentLinks }
              />
              { groups.map((group, i) => (
                <StatementLines
                  key={ i }
                  lines={ group.lines }
                  heading={ group.heading }
                  columnSettings={ columnSettings }
                  downloadingInvoices={ downloadingInvoices }
                  onDownload={ onDownloadInvoice }
                  { ...lineProps }
                />
              )) }
            </table>
          </div>

          { isOutstanding && (
            <div className={ `mt-2 flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3.5 py-3 font-semibold tabular-nums text-gray-900` }>
              <span className={ `flex flex-col gap-0.5` }>
                <span className={ `text-sm` }>{ `Balance` }</span>
                <span className={ `text-xs font-normal text-gray-500` }>{ `This may differ from the total outstanding due to credit notes.` }</span>
              </span>
              <span className={ `text-[0.9375rem]` }>{ formatAmount(statement.totalOutstandingInCents, statement.currency) }</span>
            </div>
          ) }
        </div>
      </LoadingOverlay>
    </div>
  );
}
